import React, { useState, useEffect, useRef } from 'react';
import {
  IonPage,
  IonContent,
  IonButtons,
  IonButton,
  IonIcon,
  IonSpinner,
  IonText,
  IonToast,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonBadge,
  IonProgressBar,
  IonLoading,
  IonSkeletonText,
} from '@ionic/react';
import {
  chevronBackOutline,
  pricetagOutline,
  barChartOutline,
  timeOutline,
  sparklesOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
  refreshOutline,
  openOutline,
} from 'ionicons/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { useAuth } from '../context/AuthContext';
import { subscriptionsApi, type PlanOption } from '../services/subscriptionsApi';
import {
  appleIapApi,
  APPLE_MANAGE_SUBSCRIPTIONS_URL,
  googlePlayBillingApi,
  GOOGLE_MANAGE_SUBSCRIPTIONS_URL,
} from '../services/appleIapApi';
import type { MySubscription } from '../types/subscription';
import HeaderLogo from '../components/common/HeaderLogo';
import AppHeader from '../components/common/AppHeader';
import { PageTitle } from '../components/common/PageTitle';
import styled from 'styled-components';


// Local plan option for UI (price_id from API, perMonth and currency_symbol for display)
interface LocalPlanOption extends PlanOption {
  priceId: string;
  perMonth: number;
}

declare global {
  interface Window {
    CdvPurchase?: any;
  }
}

const Subscribe: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();
  const capacitorPlatform = Capacitor.getPlatform();
  const isIOSNative = capacitorPlatform === 'ios' && Capacitor.isNativePlatform();
  const isAndroidNative = capacitorPlatform === 'android' && Capacitor.isNativePlatform();
  const isNativeStoreBilling = isIOSNative || isAndroidNative;
  const nativeStoreName = isIOSNative ? 'App Store' : 'Google Play';
  const nativePlansPlatform = isIOSNative ? 'ios' : isAndroidNative ? 'android' : undefined;
  const iapInitializedRef = useRef(false);
  const [subscription, setSubscription] = useState<MySubscription | null | undefined>(undefined);
  const [planOptions, setPlanOptions] = useState<LocalPlanOption[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [loadingCheckout, setLoadingCheckout] = useState<string | null>(null);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [loadingRestore, setLoadingRestore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkoutNotice, setCheckoutNotice] = useState<string | null>(null);
  const [iapReady, setIapReady] = useState(false);
  const [iapPrices, setIapPrices] = useState<Record<string, string>>({});
  const latestRequestRef = useRef(0);
  const checkoutSyncSessionRef = useRef<string | null>(null);

  const availablePlans = planOptions;
  const [selectedPlan, setSelectedPlan] = useState<LocalPlanOption | null>(null);

  useEffect(() => {
    if (!loadingPlans) {
      setShowLoadingOverlay(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setShowLoadingOverlay(true);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [loadingPlans]);

  const refreshSubscriptionState = async () => {
    const subRes = await subscriptionsApi.getMySubscription();
    setSubscription(subRes.data ?? null);
  };

  const getNativePurchasePlatform = (purchase: any) => {
    if (isIOSNative) return purchase.Platform?.APPLE_APPSTORE;
    if (isAndroidNative) return purchase.Platform?.GOOGLE_PLAY;
    return null;
  };

  const getTransactionProductId = (transaction: any, fallbackProductId?: string) => (
    transaction?.products?.[0]?.id ||
    transaction?.productId ||
    transaction?.receipt?.products?.[0]?.id ||
    transaction?.receipt?.productId ||
    fallbackProductId
  );

  const getGooglePurchaseToken = (transaction: any) => (
    transaction?.purchaseToken ||
    transaction?.receipt?.purchaseToken ||
    transaction?.receipt?.nativePurchase?.purchaseToken ||
    transaction?.nativePurchase?.purchaseToken ||
    transaction?.transactionId ||
    transaction?.id
  );

  const initializeNativeIap = async (productIds: string[]) => {
    if (!isNativeStoreBilling || iapInitializedRef.current) return;
    const purchase = window.CdvPurchase;
    if (!purchase || !purchase.store) {
      setError('In-app purchase plugin is not available on this build.');
      return;
    }

    const platform = getNativePurchasePlatform(purchase);
    if (!platform) {
      setError(`${nativeStoreName} billing platform is not available.`);
      return;
    }

    try {
      const store = purchase.store;
      const uniqueIds = Array.from(new Set(productIds.filter(Boolean)));
      if (uniqueIds.length === 0) return;
      iapInitializedRef.current = true;

      // Optional: set to DEBUG (4) in development to see plugin load/ready logs
      if (typeof store.verbosity !== 'undefined' && (import.meta as any).env?.DEV) {
        store.verbosity = (purchase as any).LogLevel?.DEBUG ?? 4;
      }

      store.register(
        uniqueIds.map((id) => ({
          id,
          type: purchase.ProductType?.PAID_SUBSCRIPTION ?? 'paid subscription',
          platform,
        }))
      );

      store.error((purchaseError: any) => {
        const message = purchaseError?.message || 'In-app purchase failed';
        setError(message);
      });

      store.when().approved(async (transaction: any) => {
        try {
          if (isIOSNative) {
            const transactionId = transaction?.transactionId || transaction?.id;
            const originalTransactionId = transaction?.originalTransactionId;
            if (!transactionId) {
              throw new Error('Missing Apple transaction ID');
            }
            await appleIapApi.verifyTransaction({ transactionId, originalTransactionId });
          } else {
            const productId = getTransactionProductId(transaction, selectedPlan?.priceId);
            const purchaseToken = getGooglePurchaseToken(transaction);
            const orderId = transaction?.orderId || transaction?.receipt?.orderId;
            if (!productId || !purchaseToken) {
              throw new Error('Missing Google Play purchase token.');
            }
            await googlePlayBillingApi.verifyPurchase({ productId, purchaseToken, orderId });
          }
          await refreshSubscriptionState();
          setCheckoutNotice(`Subscription successful. Your ${nativeStoreName} purchase is verified.`);
          if (typeof transaction?.finish === 'function') {
            await transaction.finish();
          }
        } catch (verifyError: any) {
          setError(verifyError?.message || `Failed to verify ${nativeStoreName} purchase.`);
        }
      });

      await store.initialize([platform]);
      // Wait for the store to finish loading products from the native layer (Apple).
      // Without this, store.get() runs before the "InAppPurchase load" response arrives.
      await new Promise<void>((resolve) => {
        if (store.isReady) {
          resolve();
          return;
        }
        store.ready(() => resolve());
      });

      const priceMap: Record<string, string> = {};
      const missingIds: string[] = [];
      uniqueIds.forEach((id) => {
        const product = store.get(id, platform);
        if (!product) {
          missingIds.push(id);
          console.warn(`[IAP] Product not found in ${nativeStoreName}: "${id}"`);
          return;
        }
        console.log(`[IAP] Product found: "${id}"`, product?.title);
        const localized =
          product?.offers?.[0]?.pricingPhases?.[0]?.price ||
          product?.offers?.[0]?.pricingPhases?.[0]?.formattedPrice ||
          product?.pricing?.price ||
          '';
        if (localized) priceMap[id] = localized;
      });

      if (missingIds.length > 0) {
        console.error(`[IAP] ${missingIds.length} product(s) not found in ${nativeStoreName}: ${missingIds.join(', ')}`);
        if (missingIds.length === uniqueIds.length) {
          setError(`No ${nativeStoreName} products found. Verify these Product IDs exist in the store console: ${missingIds.join(', ')}`);
        }
      }

      if (Object.keys(priceMap).length > 0) {
        setIapPrices(priceMap);
      }
      setIapReady(true);
    } catch (initError: any) {
      iapInitializedRef.current = false;
      setError(initError?.message || `Unable to initialize ${nativeStoreName} purchases.`);
    }
  };

  const startNativePurchase = async (productId: string) => {
    const purchase = window.CdvPurchase;
    if (!purchase?.store) {
      throw new Error('In-app purchase plugin is not available.');
    }
    const platform = getNativePurchasePlatform(purchase);
    const store = purchase.store;
    const product = store.get(productId, platform);
    if (!product) {
      throw new Error(`${nativeStoreName} product "${productId}" is unavailable. Ensure this Product ID exists and is approved.`);
    }

    const offer = (typeof product.getOffer === 'function' && product.getOffer()) || product.offers?.[0];
    if (offer?.order) {
      await offer.order();
      return;
    }
    if (store.order) {
      await store.order(productId, platform);
      return;
    }
    throw new Error(`Unable to start ${nativeStoreName} purchase flow.`);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkoutStatus = params.get('checkout');
    const sessionId = params.get('session_id');

    // When handling checkout success flow, skip initial loader fetch to avoid races.
    if (checkoutStatus === 'success' && sessionId) {
      return;
    }

    if (user?.role !== 'SubscriptionUser') {
      history.replace('/');
      return;
    }
    const requestId = ++latestRequestRef.current;
    setLoadingPlans(true);
    setError(null);
    Promise.all([
      subscriptionsApi.getMySubscription(),
      subscriptionsApi.getPlans(nativePlansPlatform),
    ])
      .then(([subRes, plansRes]) => {
        if (requestId !== latestRequestRef.current) return;
        setSubscription(subRes.data ?? null);
        const plans = (plansRes.data?.plans ?? []).map((p) => ({
          ...p,
          priceId: p.price_id,
          perMonth: p.per_month,
        }));
        setPlanOptions(plans);
        if (plans.length > 0) {
          const def = plans.find((p) => p.popular) ?? plans[plans.length - 1];
          setSelectedPlan(def);
        }
        if (isNativeStoreBilling) {
          void initializeNativeIap(plans.map((p) => p.priceId));
        }
      })
      .catch(() => {
        if (requestId !== latestRequestRef.current) return;
        setSubscription(null);
        setPlanOptions([]);
      })
      .finally(() => {
        if (requestId === latestRequestRef.current) {
          setLoadingPlans(false);
        }
      });
  }, [user?.role, history, location.search, nativePlansPlatform, isNativeStoreBilling]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkoutStatus = params.get('checkout');
    const sessionId = params.get('session_id');
    if (checkoutStatus === 'success') {
      setCheckoutNotice('Subscription successful. Your plan is being updated.');
      if (sessionId) {
        // Prevent duplicate sync calls for same session (e.g. StrictMode effect re-run).
        if (checkoutSyncSessionRef.current === sessionId) {
          return;
        }
        checkoutSyncSessionRef.current = sessionId;
        const requestId = ++latestRequestRef.current;
        setLoadingPlans(true);
        subscriptionsApi
          .syncCheckoutStatus(sessionId)
          .then(() => subscriptionsApi.getMySubscription())
          .then((subRes) => {
            if (requestId !== latestRequestRef.current) return;
            setSubscription(subRes.data ?? null);
            setError(null);
          })
          .catch((e: unknown) => {
            if (requestId !== latestRequestRef.current) return;
            const err = e as { response?: { data?: { message?: string } }; message?: string };
            const msg = err.response?.data?.message ?? err.message ?? 'Failed to refresh subscription after checkout';
            setError(typeof msg === 'string' ? msg : 'Failed to refresh subscription after checkout');
          })
          .finally(() => {
            if (requestId === latestRequestRef.current) {
              setLoadingPlans(false);
            }
            if (checkoutSyncSessionRef.current === sessionId) {
              checkoutSyncSessionRef.current = null;
            }
          });
      }
    } else if (checkoutStatus === 'cancel') {
      setCheckoutNotice('Checkout was canceled.');
    } else {
      setCheckoutNotice(null);
    }
  }, [location.search]);

  const handleCancel = async () => {
    if (subscription?.payment_provider === 'apple' || subscription?.payment_provider === 'google') {
      await Browser.open({
        url: subscription.payment_provider === 'apple'
          ? APPLE_MANAGE_SUBSCRIPTIONS_URL
          : GOOGLE_MANAGE_SUBSCRIPTIONS_URL,
      });
      return;
    }
    setError(null);
    setLoadingCancel(true);
    try {
      const res = await subscriptionsApi.cancelSubscription();
      setCheckoutNotice(res.message ?? 'Subscription will cancel at end of billing period.');
      setSubscription((prev) =>
        prev
          ? {
              ...prev,
              current_period_end: res.current_period_end ?? prev.current_period_end,
              status: res.status ?? prev.status,
              can_cancel: false,
              cancel_at_period_end: true,
            }
          : null
      );
      setError(null);
      // Refresh subscription to get updated state
      subscriptionsApi.getMySubscription().then((r) => setSubscription(r.data ?? null));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      const msg = err.response?.data?.message ?? err.message ?? 'Failed to cancel';
      setError(typeof msg === 'string' ? msg : 'Failed to cancel');
    } finally {
      setLoadingCancel(false);
    }
  };

  const handleRestorePurchases = async () => {
    if (!isNativeStoreBilling) return;
    const purchase = window.CdvPurchase;
    if (!purchase?.store?.restorePurchases) {
      setError('Restore Purchases is unavailable in this build.');
      return;
    }

    setLoadingRestore(true);
    setError(null);
    try {
      await purchase.store.restorePurchases();
      await refreshSubscriptionState();
      setCheckoutNotice(`Restore initiated. Your ${nativeStoreName} purchases are being synced.`);
    } catch (restoreError: any) {
      setError(restoreError?.message || 'Failed to restore purchases.');
    } finally {
      setLoadingRestore(false);
    }
  };

  const handleSubscribe = async () => {
    if (!selectedPlan?.priceId) return;
    setError(null);
    setLoadingCheckout(selectedPlan.priceId);
    try {
      if (isNativeStoreBilling) {
        if (!iapReady) {
          throw new Error('In-app purchase initialization is still in progress.');
        }
        await startNativePurchase(selectedPlan.priceId);
        return;
      }
      const deepLinkBase = (import.meta as any).env?.VITE_CONTRACTOR_APP_DEEP_LINK_URL as string | undefined;
      const normalizedDeepLinkBase = deepLinkBase?.trim()?.replace(/\/$/, '');
      const normalizedWebBase = window.location.origin.replace(/\/$/, '');
      const returnBase = Capacitor.isNativePlatform() && normalizedDeepLinkBase
        ? normalizedDeepLinkBase
        : normalizedWebBase;
      const successUrl = `${returnBase}/subscribe?checkout=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${returnBase}/subscribe?checkout=cancel`;
      const { checkout_url } = await subscriptionsApi.createCheckout(selectedPlan.priceId, {
        successUrl,
        cancelUrl,
      });
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: checkout_url });
      } else {
        window.location.assign(checkout_url);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      const msg = err.response?.data?.message ?? err.message ?? 'Failed to start checkout';
      setError(typeof msg === 'string' ? msg : 'Failed to start checkout');
    } finally {
      setLoadingCheckout(null);
    }
  };

  const formatPlanLabel = (planSlug?: string | null): string => {
    if (!planSlug) return 'Unknown';
    return planSlug
      .split(/[_-]/g)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  };

  const getDisplayPlanName = (sub: MySubscription): string => {
    if (sub.status === 'trialing') return 'Free Trial';
    return formatPlanLabel(sub.plan_slug);
  };

  const usagePercent = (sub: MySubscription): number => {
    if (sub.requests_limit == null || sub.requests_limit <= 0) return 0;
    return Math.min(1, sub.requests_used / sub.requests_limit);
  };

  const getPlanPriceLabel = (plan: LocalPlanOption): string => {
    if (isNativeStoreBilling && iapPrices[plan.priceId]) {
      return iapPrices[plan.priceId];
    }
    return `${plan.currency_symbol ?? '$'}${plan.price.toFixed(2)}`;
  };

  const expiryDate = subscription
    ? (subscription.plan_slug === 'trial' || subscription.status === 'trialing'
      ? subscription.trial_ends_at
      : subscription.current_period_end)
    : null;

  return (
    <IonPage>
      <AppHeader>
          <IonButtons slot="start">
            <IonButton onClick={() => history.goBack()}>
              <IonIcon icon={chevronBackOutline} />
            </IonButton>
          </IonButtons>
          <HeaderLogo />
      </AppHeader>
      <IonContent className="ion-padding">
        <IonLoading
          isOpen={showLoadingOverlay}
          message={undefined}
          spinner="crescent"
        />
        <PageTitle>My Subscription</PageTitle>

        {loadingPlans ? (
          <SkeletonStack>
            <SkeletonCard>
              <IonCardHeader>
                <SkeletonLine width="40%" />
              </IonCardHeader>
              <IonCardContent>
                <SkeletonLine width="55%" />
                <SkeletonLine width="35%" />
                <SkeletonLine width="90%" height="6px" />
              </IonCardContent>
            </SkeletonCard>

            <SkeletonCard>
              <IonCardHeader>
                <SkeletonLine width="50%" />
              </IonCardHeader>
              <IonCardContent>
                <SkeletonPlan />
                <SkeletonPlan />
                <SkeletonPlan />
                <SkeletonButton />
              </IonCardContent>
            </SkeletonCard>
          </SkeletonStack>
        ) : (
          <ContentStack>
            {subscription !== undefined && (
              <StatusCard>
                <IonCardHeader>
                  <HeaderRow>
                    <LabelUppercase>
                      <IonIcon icon={pricetagOutline} />
                      CURRENT PLAN
                    </LabelUppercase>
                    <IonBadge color="medium">
                      {subscription
                        ? subscription.status === 'trialing'
                          ? 'TRIAL'
                          : formatPlanLabel(subscription.plan_slug).toUpperCase()
                        : 'NONE'}
                    </IonBadge>
                  </HeaderRow>
                </IonCardHeader>
                <IonCardContent>
                  <PlanName>
                    {subscription ? getDisplayPlanName(subscription) : 'No active plan'}
                  </PlanName>
                  {subscription && expiryDate && (
                    <ExpiryLine>
                      <IonIcon icon={timeOutline} />
                      <span>{subscription.cancel_at_period_end ? 'Cancels on ' : 'Ends on '}{new Date(expiryDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </ExpiryLine>
                  )}
                  {subscription && (
                    <UsageSection>
                      <UsageHeader>
                        <span>
                          <IonIcon icon={barChartOutline} />
                          AI Usage
                        </span>
                        <span>
                          {subscription.requests_used}
                          {subscription.requests_limit != null ? ` / ${subscription.requests_limit}` : ''}
                        </span>
                      </UsageHeader>
                      {subscription.requests_limit != null && subscription.requests_limit > 0 && (
                        <IonProgressBar
                          value={usagePercent(subscription)}
                          color={usagePercent(subscription) >= 0.8 ? 'warning' : 'primary'}
                        />
                      )}
                    </UsageSection>
                  )}
                  {subscription?.can_cancel && (
                    <CancelSection>
                      <IonButton
                        fill="outline"
                        color="medium"
                        size="small"
                        disabled={loadingCancel}
                        onClick={handleCancel}
                      >
                        {loadingCancel ? <IonSpinner name="crescent" /> : <IonIcon icon={closeCircleOutline} />}
                        {!loadingCancel && ' Cancel at period end'}
                      </IonButton>
                      <CancelHint>Access continues until {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'billing period ends'}</CancelHint>
                    </CancelSection>
                  )}
                  {(subscription?.payment_provider === 'apple' || subscription?.payment_provider === 'google') && (
                    <CancelSection>
                      <IonButton
                        fill="outline"
                        color="medium"
                        size="small"
                        onClick={handleCancel}
                      >
                        <IonIcon icon={openOutline} />
                        {subscription.payment_provider === 'apple' ? ' Manage in App Store' : ' Manage in Google Play'}
                      </IonButton>
                      <CancelHint>
                        {subscription.payment_provider === 'apple'
                          ? 'Apple subscriptions are managed in your App Store account settings.'
                          : 'Google Play subscriptions are managed in your Play Store account settings.'}
                      </CancelHint>
                    </CancelSection>
                  )}
                </IonCardContent>
              </StatusCard>
            )}

            <PlansCard>
              <SectionHeader>
                <h2>Upgrade Your Plan</h2>
                <p>Pick the right plan for your usage</p>
              </SectionHeader>

              <PlanOptions>
                {planOptions.map((plan) => {
                  const hasPriceId = !!plan.priceId;
                  const isSelected = selectedPlan?.id === plan.id;
                  return (
                    <PlanCard
                      key={plan.id}
                      $selected={isSelected}
                      $popular={!!plan.popular}
                      onClick={() => hasPriceId && setSelectedPlan(plan)}
                      $disabled={!hasPriceId}
                    >
                      {plan.popular && (
                        <PopularBadge>Most Popular</PopularBadge>
                      )}
                      <PlanCardInner>
                        <PlanLeft>
                          <PlanNameLarge>{plan.name}</PlanNameLarge>
                          <PlanDescription>{plan.description}</PlanDescription>
                        </PlanLeft>
                        <PlanRight>
                          <PlanPriceBlock>
                            <PlanPrice>{getPlanPriceLabel(plan)}</PlanPrice>
                            <PlanPerMonth>
                              {isIOSNative
                                ? 'Billed monthly by Apple'
                                : isAndroidNative
                                  ? 'Billed monthly by Google Play'
                                  : `${(plan.currency_symbol ?? '$') + plan.perMonth.toFixed(2)} / month`}
                            </PlanPerMonth>
                          </PlanPriceBlock>
                          {isSelected && <SelectedIcon icon={checkmarkCircleOutline} />}
                        </PlanRight>
                      </PlanCardInner>
                    </PlanCard>
                  );
                })}
              </PlanOptions>

              <SubscribeButton
                expand="block"
                size="large"
                disabled={!selectedPlan?.priceId || !!loadingCheckout || (isNativeStoreBilling && !iapReady)}
                onClick={handleSubscribe}
              >
                {loadingCheckout
                  ? <IonSpinner name="crescent" />
                  : isNativeStoreBilling
                    ? `Subscribe in ${nativeStoreName}`
                    : 'Subscribe Now'}
                {!loadingCheckout && <IonIcon icon={sparklesOutline} slot="end" />}
              </SubscribeButton>

              {isNativeStoreBilling && (
                <>
                  <SubscribeButton
                    expand="block"
                    fill="outline"
                    color="#CCC"
                    disabled={loadingRestore}
                    onClick={handleRestorePurchases}
                  >
                    {loadingRestore ? <IonSpinner name="crescent" /> : 'Restore Purchases'}
                    {!loadingRestore && <IonIcon icon={refreshOutline} slot="end" />}
                  </SubscribeButton>

                  <ComplianceText>
                    Payment is charged by {nativeStoreName} at confirmation. Subscription auto-renews unless canceled
                    before the end of the current period.
                  </ComplianceText>
                  <ComplianceLinks>
                    <a href="https://www.smart-docs.io/terms" target="_blank" rel="noreferrer">Terms of Service</a>
                    <a href="https://www.smart-docs.io/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>
                  </ComplianceLinks>
                </>
              )}
            </PlansCard>

            {isNativeStoreBilling && !iapReady && !loadingPlans && (
              <IonText color="medium">
                <p className="ion-text-center">Connecting to {nativeStoreName} products...</p>
              </IonText>
            )}

            {availablePlans.length === 0 && (
              <IonText color="medium">
                <p className="ion-text-center">Checkout is not configured. Contact support.</p>
              </IonText>
            )}
          </ContentStack>
        )}
      </IonContent>

      <IonToast
        isOpen={!!error}
        message={error ?? ''}
        color="danger"
        position="top"
        buttons={[{ text: 'Dismiss', role: 'cancel' }]}
        onDidDismiss={() => setError(null)}
      />
      <IonToast
        isOpen={!!checkoutNotice}
        message={checkoutNotice ?? ''}
        color="success"
        position="top"
        duration={5000}
        onDidDismiss={() => setCheckoutNotice(null)}
      />
    </IonPage>
  );
};

export default Subscribe;

// Styled components
const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 24px;
`;

const SkeletonCard = styled(IonCard)`
  margin: 0;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
`;

const SkeletonLine = styled(IonSkeletonText)<{ width?: string; height?: string }>`
  width: ${(p) => p.width || '100%'};
  height: ${(p) => p.height || '16px'};
  margin: 8px 0;
`;

const SkeletonPlan = styled(IonSkeletonText)`
  height: 68px;
  border-radius: 10px;
  margin: 10px 0;
`;

const SkeletonButton = styled(IonSkeletonText)`
  height: 44px;
  border-radius: 10px;
  margin-top: 14px;
`;

const ContentStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 32px;
`;

const StatusCard = styled(IonCard)`
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  margin: 0;

  ion-card-header {
    padding-bottom: 0;
  }

  ion-card-content {
    padding-top: 8px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  ion-icon {
    margin-right: 6px;
    vertical-align: middle;
    font-size: 14px;
  }
`;

const LabelUppercase = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: var(--ion-color-medium);
  text-transform: uppercase;
  display: flex;
  align-items: center;
`;

const PlanName = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: var(--ion-text-color);
  margin-bottom: 4px;
`;

const ExpiryLine = styled.div`
  font-size: 13px;
  color: var(--ion-color-medium);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 6px;

  ion-icon {
    font-size: 16px;
  }
`;

const CancelSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--ion-color-light-shade);

  ion-button {
    --padding-start: 12px;
    --padding-end: 12px;
  }

  ion-button ion-icon {
    margin-right: 6px;
    vertical-align: middle;
  }
`;

const CancelHint = styled.div`
  font-size: 12px;
  color: var(--ion-color-medium);
  margin-top: 8px;
`;

const UsageSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--ion-color-light-shade);

  ion-progress-bar {
    height: 6px;
    border-radius: 3px;
    margin-top: 8px;
  }
`;

const UsageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: var(--ion-color-medium);

  span:first-child {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  span:last-child {
    font-weight: 600;
    color: var(--ion-text-color);
  }

  ion-icon {
    font-size: 16px;
  }
`;

const PlansCard = styled(IonCard)`
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  margin: 0;
  padding: 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 20px;

  h2 {
    font-size: 20px;
    font-weight: 700;
    color: var(--ion-text-color);
    margin: 0 0 4px 0;
  }

  p {
    font-size: 14px;
    color: var(--ion-color-medium);
    margin: 0;
  }
`;

const PlanOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

const PlanCard = styled.div<{
  $selected: boolean;
  $popular: boolean;
  $disabled: boolean;
}>`
  position: relative;
  border-radius: 10px;
  padding: 16px;
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};
  border: 2px solid
    ${(p) =>
      p.$selected
        ? 'var(--ion-color-primary)'
        : 'var(--ion-color-light-shade)'};
  background: ${(p) =>
    p.$selected
      ? 'rgba(var(--ion-color-primary-rgb), 0.06)'
      : 'var(--ion-card-background)'};
  transition: border-color 0.15s ease, background 0.15s ease;
`;

const PlanCardInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const PopularBadge = styled.span`
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--ion-color-primary);
  color: var(--ion-color-primary-contrast);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  padding: 4px 10px;
  border-radius: 6px;
  white-space: nowrap;
`;

const PlanLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PlanNameLarge = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: var(--ion-text-color);
`;

const PlanDescription = styled.div`
  font-size: 13px;
  color: var(--ion-color-medium);
`;

const PlanRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: right;
`;

const PlanPriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const PlanPrice = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--ion-text-color);
`;

const PlanPerMonth = styled.div`
  font-size: 12px;
  color: var(--ion-color-medium);
  margin-top: 2px;
`;

const SelectedIcon = styled(IonIcon)`
  font-size: 20px;
  color: var(--ion-color-primary);
`;

const SubscribeButton = styled(IonButton)`
  ion-icon {
    margin-left: 8px;
  }
`;

const ComplianceText = styled.p`
  margin: 12px 0 8px;
  font-size: 12px;
  color: var(--ion-color-medium);
  line-height: 1.45;
`;

const ComplianceLinks = styled.div`
  display: flex;
  gap: 12px;

  a {
    font-size: 12px;
    color: var(--ion-color-primary);
    text-decoration: none;
  }
`;
